import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar, Stethoscope, Sparkles, Users, Grid3X3, Scissors, Heart } from 'lucide-react';
import { heroSlides, featureCards, services, practiceInfo } from '../data/siteData';
import './Home.css';

function Home() {
  const [currentSlide, setCurrentSlide] = React.useState(0);

  React.useEffect(() => {
    const timer = setInterval(() => setCurrentSlide(prev => (prev + 1) % heroSlides.length), 6000);
    return () => clearInterval(timer);
  }, []);

  const iconMap = { Stethoscope, Sparkles, Users, Grid3X3, Scissors, Heart };

  return (
    <main>
      {/* Hero Slider */}
      <section className="hero-slider">
        {heroSlides.map((slide, index) => (
          <div key={slide.id} className={`hero-slide ${index === currentSlide ? 'active' : ''}`} style={{ backgroundImage: `url(${slide.image})` }}>
            <div className="hero-overlay"></div>
            <div className="hero-content">
              <div className="hero-text">
                <h2>{slide.title}</h2>
                {slide.highlight && <p className="hero-highlight">{slide.highlight}</p>}
                <p className="hero-description">{slide.description}</p>
                <Link to={slide.buttonLink} className="btn btn-primary">{slide.buttonText}</Link>
              </div>
            </div>
          </div>
        ))}
        <button className="hero-nav prev" onClick={() => setCurrentSlide(prev => (prev - 1 + heroSlides.length) % heroSlides.length)}><ChevronLeft size={32} /></button>
        <button className="hero-nav next" onClick={() => setCurrentSlide(prev => (prev + 1) % heroSlides.length)}><ChevronRight size={32} /></button>
        <div className="hero-dots">{heroSlides.map((_, i) => <button key={i} className={`dot ${i === currentSlide ? 'active' : ''}`} onClick={() => setCurrentSlide(i)} />)}</div>
      </section>

      {/* Feature Cards */}
      <section className="feature-cards-section">
        <div className="container">
          <div className="feature-cards">
            {featureCards.map(card => (
              <div key={card.id} className={`feature-card ${card.highlighted ? 'highlighted' : ''}`}>
                <div className="feature-card-image"><img src={card.image} alt={card.title} /></div>
                <div className="feature-card-content">
                  <h3>{card.title}</h3>
                  <p>{card.description}</p>
                  {card.link && <Link to={card.link} className="feature-link">{card.linkText}</Link>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Welcome Section */}
       <section className="welcome-section">
        <div className="container">
          <div className="welcome-content">
            <h2>Welcome to P3 Dental of Northeast Philadelphia!</h2>
			<p>At P3 Dental, we are dedicated to providing exceptional dental care for you and your family in the heart of Northeast Philadelphia. Whether you’re due for a routine check-up, cosmetic enhancement, or advanced restorative treatment, our experienced team is here to help you achieve a healthy, confident smile you’ll be proud of.</p>
			<p>We offer a wide range of dental services designed to meet the needs of patients of all ages — from children to adults. Our compassionate professionals focus on preventative, general, and cosmetic dental care, ensuring your visit is personalized, comfortable, and effective.</p>
			<p>Whether you’re coming in for a routine cleaning, Invisalign treatment, dental implants, or wisdom tooth extraction, we take the time to understand your goals and explain every step of your treatment plan.</p>
			<Link to="/contact-us" className="btn btn-primary">Contact Us</Link>
          </div>
        </div>
      </section>

      {/* Eagles Partnership Section */}
      <section className="eagles-partnership-section">
        <div className="container">
          <div className="eagles-content">
            <div className="eagles-image">
              <img src="/images/eagles.jpg" alt="P3 Dental - Official Dental Services Provider of Philadelphia Eagles" className="partnership-image" />
            </div>
            <div className="eagles-text">
              <h2>Proud Partner of the Philadelphia Eagles</h2>
              <p className="eagles-highlight">P3 Dental - Official Dentist of the Philadelphia EAGLES!!!</p>
              <p>As the <strong>Official Dental Services Provider of the Philadelphia Eagles</strong>, we are honored to provide comprehensive dental care to one of the NFL's most beloved teams. Through our partnership with Dudhat Dental Group, we deliver the same world-class dental services to the Eagles' players, coaches, and staff that we provide to our patients every day.</p>
              <p>Dr. Dilip Dudhat, a lifelong Eagles fan and Montgomery County native, leads our team in providing sports dentistry and on-field emergency dental services at every Eagles home game. We're proud to safeguard the oral health of your favorite team!</p>
              <div className="eagles-stats">
                <div className="stat-item">
                  <span className="stat-number">20+</span>
                  <span className="stat-label">Years of Excellence</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">30+</span>
                  <span className="stat-label">Locations Across PA</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">#1</span>
                  <span className="stat-label">Trusted by Champions</span>
                </div>
              </div>
              <Link to="https://philadelphiaeaglesdentist.com/" className="btn btn-primary" target='_blank'>More Info</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section">
        <div className="container">
          <div className="section-header">
            <h2>All Services Under One Roof!</h2>
            <p>Experience the Difference of Truly Personal Dental Care!</p>
          </div>
          <div className="services-grid">
            {services.map(service => {
              const Icon = iconMap[service.icon] || Stethoscope;
              return (
                <Link to={service.link} key={service.id} className="service-card">
                  <div className="service-icon"><Icon size={32} /></div>
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                </Link>
              );
            })}
          </div>
          <div className="services-cta"><Link to="/procedures" className="btn btn-outline-dark">View All Procedures</Link></div>
        </div>
      </section>

      {/* Healthy Gums Section */}
      <section className="split-section">
        <div className="split-image" style={{ backgroundImage: "url('/images/difference.jpg')" }}></div>
        <div className="split-content bg-primary">
          <h3>Why Choose P3 Dental? </h3>
          <h4>Patient-Centered Care</h4>
          <p>Your comfort and satisfaction are at the center of everything we do. We strive to create a welcoming, stress-free environment where questions are encouraged and care is tailored to you.</p>
          <h4>Experienced & Caring Team</h4>
		  <p>Our skilled dentists, hygienists, and support staff work together to deliver high-quality dental care with compassion and professionalism. From routine maintenance to advanced procedures, you’re in capable hands.</p>
		  { /*<Link to="/procedures/cleanings-prevention" className="btn btn-outline">Teeth Cleanings</Link>*/}
        </div>
      </section>

      {/* Family Respect Section */}
      <section className="split-section">
        <div className="split-content bg-light">
          <h3>Your dental health is our top priority</h3>
          <h4>A One-Stop Dental Practice</h4>
          <p>At P3 Dental, we are proud to be a one-stop destination for all your dental needs. From preventive care to advanced treatments, we’re equipped to handle every aspect of oral health under one roof — saving you time while delivering consistent, personalized care.</p>
          <h4>Join Our Dental Family</h4>
		  <p>We welcome new patients from throughout Philadelphia, Bucks County, Montgomery County, and South New Jersey. Our convenient location on Roosevelt Blvd makes it easy to find quality dental care nearby.</p>
		  <p>Let us partner with you on your journey to a healthier, brighter smile. Contact us today to schedule your appointment!</p>
		  { /*<Link to="/family-dentistry" className="btn btn-primary">Patient Information</Link>*/}
        </div>
        <div className="split-image" style={{ backgroundImage: "url('/images/priority.jpg')" }}></div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Schedule Your Appointment?</h2>
            <p>Evening and Saturday appointments available!</p>
            <Link to="/appointment-request" className="btn btn-cta"><Calendar size={20} /> Schedule Your Appointment</Link>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Home;
